import { successToast } from '@/helpers';
import { useCallback, useMemo, useState } from 'react';
import Table from '@/components/UI/Table';
import Button from '@/components/UI/Button';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { _isEmpty } from '@/littledash';
import { useDispatch } from 'react-redux';
import useCredentials from './useCredentials';
import useScopes from './useScopes';
import Header from '@/components/UI/Header';
import Loading from '@/components/Loading';
import { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';

interface Credential {
  client_name: string;
  scope: Array<string>;
  client_id: string;
  api_version: string;
}

interface CredentialDisplay extends Omit<Credential, 'scope'> {
  scope: string;
}

const ApiKeys = () => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const { credentials, refetch, revokeApiKey, loading: credentialsLoading } = useCredentials();
  const { scopes } = useScopes();

  const dispatch = useDispatch();

  const closeModal = useCallback(() => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  }, [dispatch]);

  const openModal = useCallback(
    (modal: string, props: Record<string, unknown>) => {
      dispatch({
        type: 'OPEN_MODAL',
        modal,
        props,
      });
    },
    [dispatch]
  );

  const clearErrors = useCallback(() => setApiError(false), []);
  const setErrors = useCallback(() => setApiError(true), []);
  const setNotLoading = useCallback(() => setLoading(false), []);

  const handleDeleteApiKeysClick = useCallback(
    (clientIds: Array<string>) => {
      openModal(clientIds.length > 1 ? 'CONFIRM_DELETE_API_KEYS' : 'CONFIRM_DELETE_API_KEY', {
        onClick: () => {
          setLoading(true);
          Promise.all(clientIds.map(revokeApiKey))
            .then(() => successToast(`Successfully deleted ${clientIds.length > 1 ? 'API keys' : 'API key'}.`))
            .then(clearErrors)
            .then(refetch)
            .catch(setErrors)
            .finally(setNotLoading);
        },
        closeModal,
      });
    },
    [revokeApiKey, clearErrors, refetch, setErrors, setNotLoading, openModal, closeModal]
  );

  const handleAddApiKeyClick = () => {
    openModal('ADD_API_KEY', {
      closeModal,
      handleCallback: (clientId: string, secretKey: string) => {
        clearErrors();
        closeModal();
        refetch();
        openModal('API_KEY_CREATED', {
          closeModal,
          clientId,
          secretKey,
        });
      },
      scopes,
    });
  };

  const columns = useMemo<Array<TanStackColumnDef<CredentialDisplay>>>(
    () => [
      {
        id: 'client_name',
        accessorKey: 'client_name',
        header: 'Name',
        enableSorting: true,
      },
      {
        id: 'scope',
        accessorKey: 'scope',
        header: 'Scopes',
        enableSorting: true,
      },
      {
        id: 'client_id',
        accessorKey: 'client_id',
        header: 'Client ID',
        enableSorting: true,
      },
      {
        id: 'api_version',
        accessorKey: 'api_version',
        header: 'API Version',
        enableSorting: true,
      },
    ],
    []
  );

  const tableData = useMemo<Array<CredentialDisplay>>(
    () =>
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      (credentials as Array<Credential>)?.map((credential) => ({
        ...credential,
        scope: credential.scope.join(', '),
      })) || [],
    [credentials]
  );

  const actions = useMemo(
    () => [
      {
        name: Object.keys(selectedRows)?.length > 1 ? 'Delete API keys' : 'Delete API key',
        action: () => {
          const getClientId = (rowIndex: string) => tableData[Number(rowIndex)].client_id;
          handleDeleteApiKeysClick(Object.keys(selectedRows).map(getClientId));
        },
      },
    ],
    [selectedRows, tableData, handleDeleteApiKeysClick]
  );

  const isLoading = loading || credentialsLoading;
  if (credentialsLoading && credentials?.length === 0) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div className="mb3">
        <Header mainHeaderText="API keys" />
      </div>
      <div className={`ui-card ${isLoading ? 'ui__disabled' : ''}`}>
        {apiError && <ApiErrorBanner error={apiError} />}
        {credentials?.length > 0 ? (
          <>
            <div className="flex pa3 bb b--moon-gray">
              <div className="flex w-100">
                <div className="flex mr2">
                  {!_isEmpty(selectedRows) && (
                    <div
                      className="mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1"
                      style={{
                        whiteSpace: 'nowrap',
                        lineHeight: '2.4rem',
                      }}
                    >
                      {Object.keys(selectedRows).length} selected
                    </div>
                  )}
                  <SelectDropDown
                    title="Actions"
                    className={`plain f6 ${!_isEmpty(selectedRows) ? 'br--right' : ''}`}
                    alignMenu="right"
                    disabled={_isEmpty(selectedRows)}
                  >
                    <ActionList actions={actions} />
                  </SelectDropDown>
                </div>
              </div>
              <Button onClick={handleAddApiKeyClick}>Create API key</Button>
            </div>
            <Table data={tableData} columns={columns} selectedRows={selectedRows} setSelectedRows={setSelectedRows} />
          </>
        ) : (
          <div className="w-100 flex flex-column justify-center items-center" style={{ height: '400px' }}>
            <h3 className="normal lh-title f4 near-black pb4">Your API keys will appear here.</h3>
            <Button onClick={handleAddApiKeyClick}>Add an API key</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
